import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    CourseParticipation, CourseParticipationStatus,
    CourseStatus,
    CourseType,
    LearnModuleType,
} from '@cna/projects/ak-jura/shared/interfaces';
import { filterNil } from '@cna/shared/helper';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { map, skipWhile, takeUntil, withLatestFrom } from 'rxjs/operators';
import { setBreadcrumbs } from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/breadcrumb/actions';
import {
    loadControlTestForActualCourse,
    resetControlTestValidationResult,
} from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/controlTests/actions';
import {
    checkCourseStatus,
    migrateCourse,
} from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/course/actions';
import { selectMigrationInProgress } from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/course/selectors';
import { loadCourseParticipationInfos } from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/course_participation/actions';
import {
    selectCurrentCourseType,
    selectCustomersCourseParticipation,
} from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/course_participation/selectors';
import { updateCustomerConsents } from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/customer/actions';
import { selectCustomerLearnTimeInfoRead } from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/customer/selectors';
import { loadModuleInfosForModuleId } from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/module_infos/actions';
import {
    selectControlTestPassed,
    selectCurrentModuleSequentialNumber,
    selectModuleInfo,
    selectModuleLoading,
    selectMustUpdateData,
} from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/module_infos/selectors';
import { selectModuleNotesByModuleIdFromRouter } from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/notes/selectors';
import {
    selectCourseParticipationId,
    selectModuleId,
} from '../../../../../../../../../apps/ak-jura/frontends/my-ak-jura/src/app/+state/router.selector';
import { NumberingService } from '../../../../../../../../angular/services/src/lib/numbering/numbering.service';
import { NumberToNumberingPipe } from '../../../../../../../../angular/ui/pipes/src/lib/number-to-numbering.pipe';
import { Consents } from '../../../../../../shared/interfaces/src/lib/enums';
import { LearningModuleDocumentInfo } from '../../../../../../shared/interfaces/src/lib/learning-module-document-info';
import { ModuleDocumentHelperService } from '../../../../../shared-components/src/lib/services/module-document-helper.service';
import { CertificateService } from '../services/certificate.service';

@Component({
    selector: 'cna-learning-module-overview-view',
    templateUrl: './learning-module-overview-view.component.html',
    styleUrls: ['./learning-module-overview-view.component.scss'],
})
export class LearningModuleOverviewViewComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private _destroy$ = new Subject<void>();

    public loading$ = combineLatest([
        this.store.select(selectModuleLoading),
        this.store.select(selectMigrationInProgress),
    ]).pipe(
        map(
            ([moduleLoading, migrationInProgress]) =>
                moduleLoading || migrationInProgress
        )
    );
    public module$: Observable<LearningModuleDocumentInfo> = this.store
        .select(selectModuleInfo)
        .pipe(
            takeUntil(this._destroy$),
            skipWhile(v => !v)
        );
    public moduleName$: Observable<string> = this.module$.pipe(
        map(module => module.moduleName)
    );
    public courseParticipation$: Observable<CourseParticipation> = this.store
        .select(selectCustomersCourseParticipation)
        .pipe(
            takeUntil(this._destroy$),
            skipWhile(v => !v)
        );
    public courseType$ = this.courseParticipation$.pipe(
        map(courseParticipation => courseParticipation?.bookedCourse?.type)
    );
    public courseName$ = this.courseParticipation$.pipe(
        map(courseParticipation => courseParticipation.bookedCourse.description)
    );

    public CourseType = CourseType;
    public LearnModuleType = LearnModuleType;

    moduleDocumentInfo$: Observable<LearningModuleDocumentInfo> = this.store
        .select(selectModuleInfo)
        .pipe(
            skipWhile(mI => !mI),
            map(mI => {
                return mI;
            })
        );

    controlTestPassed$: Observable<boolean> = this.store
        .select(selectControlTestPassed)
        .pipe(takeUntil(this._destroy$));

    notes$ = this.store
        .select(selectModuleNotesByModuleIdFromRouter)
        .pipe(takeUntil(this._destroy$));

    mustUpdate$ = this.store.select(selectMustUpdateData).pipe(
        filterNil(),
        map(
            ({ status, moduleInfo, courseParticipation }) =>
                this.moduleDocumentHelperService.getPercentageReadForNodeModul(
                    moduleInfo
                ) === 0 && status === CourseStatus.DEPRECATED && (courseParticipation.status === CourseParticipationStatus.EDUCATION_REPETITORIUM || moduleInfo.moduleType !== LearnModuleType.Repetitorium),
        ),
        takeUntil(this._destroy$)
    );
    protected courseParticipationId$ = this.store
        .select(selectCourseParticipationId)
        .pipe(filterNil());

    protected learnTimeInfoReadConsent = false;
    protected savedLearnTimeInfoReadConsent = false;

    @ViewChild('learnTimeInfoModal') learnTimeInfoModal: ElementRef;

    constructor(
        private store: Store,
        private router: Router,
        private route: ActivatedRoute,
        private moduleDocumentHelperService: ModuleDocumentHelperService,
        private numberingService: NumberingService,
        private numberToNumberingPipe: NumberToNumberingPipe,
        private modalService: NgbModal,
        private certificateService: CertificateService
    ) {}

    ngOnInit(): void {
        this.store.dispatch(loadCourseParticipationInfos());
        this.store.dispatch(loadModuleInfosForModuleId({}));
        this.store.dispatch(checkCourseStatus());
        this.setBreadcrumbs();
    }

    ngAfterViewInit(): void {
        this.store
            .select(selectCustomerLearnTimeInfoRead)
            .pipe(
                filterNil(),
                withLatestFrom(this.courseType$),
                takeUntil(this._destroy$)
            )
            .subscribe(([consent, courseType]) => {
                this.learnTimeInfoReadConsent = consent;
                this.savedLearnTimeInfoReadConsent = consent;
                if (!consent && courseType === CourseType.EducationCourse) {
                    this.showLearnTimeInfoModal();
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        if (this.modalService.hasOpenModals()) {
            this.modalService.dismissAll();
        }
    }

    setBreadcrumbs() {
        combineLatest([
            this.store.select(selectCourseParticipationId).pipe(filterNil()),
            this.store.select(selectModuleId).pipe(filterNil()),
            this.store.select(selectCurrentCourseType).pipe(filterNil()),
            this.store.select(selectModuleInfo).pipe(filterNil()),
        ])
            .pipe(takeUntil(this._destroy$))
            .subscribe(
                ([
                    courseParticipationId,
                    moduleId,
                    courseType,
                    moduleInfo,
                ]) => {
                    this.store.dispatch(
                        setBreadcrumbs({
                            breadcrumbs: [
                                {
                                    label: 'Mein AK JURA',
                                    routerLink: courseParticipationId,
                                },
                                {
                                    label: 'Lernstatus',
                                    routerLink:
                                        courseParticipationId +
                                        '/learning/courseOverview',
                                },
                                {
                                    label:
                                        courseType === CourseType.TrainingCourse
                                            ? 'Inhalt'
                                            : `${moduleInfo.moduleName}`,
                                    routerLink:
                                        courseParticipationId +
                                        '/learning/moduleOverview/' +
                                        moduleId,
                                },
                            ],
                        })
                    );
                }
            );
    }

    async startLearnControl(): Promise<void> {
        this.store.dispatch(resetControlTestValidationResult());
        this.store.dispatch(loadControlTestForActualCourse());
        await this.router.navigate(['learningControl'], {
            relativeTo: this.route,
        });
    }

    /** readStatus only in level section without subsection  */
    isModuleReadAndMarkedDone(): Observable<boolean> {
        return this.moduleDocumentInfo$.pipe(
            takeUntil(this._destroy$),
            map(mi => {
                let readChapter = 0;
                let allRelevantNodes = 0;
                for (let i = 0; i < mi.chapters.length; i++) {
                    for (let k = 0; k < mi.chapters[i].sections?.length; k++) {
                        if (mi.chapters[i].sections[k]?.readStatus?.read) {
                            readChapter++;
                            allRelevantNodes++;
                        } else {
                            for (
                                let j = 0;
                                j <
                                mi.chapters[i].sections[k]?.sections?.length;
                                j++
                            ) {
                                if (
                                    mi.chapters[i].sections[k]?.sections[j]
                                        .readStatus?.read
                                ) {
                                    readChapter++;
                                    allRelevantNodes++;
                                } else {
                                    allRelevantNodes++;
                                }
                            }
                        }
                    }
                }

                if (
                    readChapter === allRelevantNodes &&
                    mi.totalTimeRead >= mi.targetReadingTime
                ) {
                    return true;
                }
                if (
                    readChapter !== allRelevantNodes ||
                    mi.totalTimeRead >= mi.targetReadingTime
                ) {
                    return false;
                }
            })
        );
    }

    protected migrate(): void {
        this.store.dispatch(migrateCourse());
    }

    private showLearnTimeInfoModal(): void {
        this.modalService.open(this.learnTimeInfoModal, {
            backdrop: 'static',
            keyboard: false,
        });
    }

    protected acceptLearnTimeInfo(modal: NgbModalRef): void {
        modal.close();
        if (
            this.learnTimeInfoReadConsent !== this.savedLearnTimeInfoReadConsent
        ) {
            this.store.dispatch(
                updateCustomerConsents({
                    consents: {
                        [Consents.LEARN_TIME_INFO_READ]:
                            this.learnTimeInfoReadConsent,
                    },
                })
            );
        }
    }

    protected downloadLearnControlPdf(
        module: LearningModuleDocumentInfo,
        courseParticipation: CourseParticipation
    ): void {
        const cpModule = courseParticipation.bookedCourse.modules.find(
            m => m.id === module.moduleId
        );
        if (!cpModule) {
            throw new Error(
                'Could not find the course participation id for the control test of this module'
            );
        }
        this.certificateService.downloadLearnControlPdf(
            {
                ...module,
                controlTestId: cpModule.controlTestId,
                controlTestCourseParticipationId:
                    cpModule.controlTestCourseParticipationId,
            },
            courseParticipation.bookedCourse.type
        );
    }
}
